import { useEffect, useRef, useState } from 'react';
import { hapticsImpactLight } from '../../../utils/haptics';
import { useSelector } from 'react-redux';
import { selectTodaySession } from '../../../slices/todaySession/todaySessionSlice';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { ArrowRight, Bell, Clock, Settings } from 'react-feather';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AnimatedCheckmark from '../../Global/AnimatedCheckmark';
import {
  selectSubmittedVignettesCount,
  selectAnsweredVignettesCount,
  selectStartedVignettesCount,
  selectActiveBlock,
} from '../../../slices/blocks/blocksSlice';
import { selectCompletedFlashcardsCount } from '../../../slices/flashcards/flashcardsSlice';
import { RootState } from '../../../store/store';
import { updateCurrentUserInfo } from '../../../services/auth';
import { enqueueSnackbar } from 'notistack';
import posthog from 'posthog-js';
import { updateStudySession } from '../../../services/studySession';
import { DotsPattern } from '../../../assets/svgs/DotsPattern';
import ContentType from '../ContentType';
import LecturePanel from '../LecturePanel';
import { modifyStudySession } from '../../../services/study';
import moment from 'moment';
import { BlockType } from '../../../types/Study';
import { useStudyPhaseSend } from '../../../studyPhase';
import { useRefreshStudySessionAndContent } from '../../../utils/refreshStudySession';
import { isSuperAdmin } from '../../../utils/isSuperAdmin';
import { selectVideos } from '../../../slices/videos/videosSlice';
import { useUserExamUpdate } from '../../../utils/updateUserExam';
import { createPortal } from 'react-dom';
import useWindowWidth from '../../../hooks/useWindowWidth';

interface TodaySessionProps {
  isNoContent: boolean;
}

const TodaySession: React.FC<TodaySessionProps> = ({ isNoContent }) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const todaySession = useSelector(selectTodaySession);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const stepExams = useSelector(examsSelectors.selectStepExams);
  const activeBlock = useSelector(selectActiveBlock);
  const submittedVignettesCount = useSelector(selectSubmittedVignettesCount);
  const answeredVignettesCount = useSelector(selectAnsweredVignettesCount);
  const completedFlashcardsCount = useSelector(selectCompletedFlashcardsCount);
  const modifyQBankMinimum = useSelector(selectStartedVignettesCount);

  const videos = useSelector(selectVideos);

  const [isEditingCustomSession, setIsEditingCustomSession] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [customFlashcardCount, setCustomFlashcardCount] = useState(
    todaySession?.flashcardCount || 0
  );
  const [customQBankCount, setCustomQBankCount] = useState(
    todaySession?.vignetteCount || 0
  );
  const [isSessionSettingsMenuOpen, setIsSessionSettingsMenuOpen] =
    useState(false);

  const sessionSettingsRef = useRef<HTMLButtonElement>(null);

  const { sendPhaseEvent } = useStudyPhaseSend();
  const windowWidth = useWindowWidth();

  const { refreshStudySessionAndContent } = useRefreshStudySessionAndContent();

  const { updateExam } = useUserExamUpdate(refreshStudySessionAndContent, {
    showSuccessMessage: false,
  });

  useEffect(() => {
    setCustomFlashcardCount(todaySession?.flashcardCount);
    setCustomQBankCount(todaySession?.vignetteCount);
  }, [todaySession, isEditingCustomSession]);

  useEffect(() => {
    if (isEditingCustomSession) {
      if (
        (primaryExam?.isUsingFlashcards &&
          customFlashcardCount !== todaySession?.flashcardCount) ||
        (primaryExam?.isUsingQbank &&
          customQBankCount !== todaySession?.vignetteCount)
      ) {
        setIsSaveDisabled(false);
      } else {
        setIsSaveDisabled(true);
      }
    }
  }, [
    todaySession,
    isEditingCustomSession,
    customFlashcardCount,
    customQBankCount,
  ]);

  const handleSubmitCustomSession = async () => {
    if (!primaryExam?.id || !currentUser?.id) {
      return;
    }
    await modifyStudySession(
      todaySession.id,
      customFlashcardCount,
      customQBankCount
    );
    await refreshStudySessionAndContent();
    setIsEditingCustomSession(false);
  };

  const handleSnoozeSession = async () => {
    await updateStudySession({
      id: todaySession.id,
      isSnoozed: true,
    });
    posthog?.capture('study_session_snooze');
  };

  const turnOnDailyStudyReminders = async () => {
    await updateCurrentUserInfo({
      studyReminders: 'daily',
      subscribedToEmails: true,
    });
    enqueueSnackbar('Daily study reminders turned on.', {
      autoHideDuration: 3000,
    });
  };

  const renderAssessmentCompleteInstructions = () => {
    if (currentUser?.cohort === 'rct' && currentUser?.rctGroup === 'A') {
      const stepExam = stepExams[0];
      if (!stepExam) {
        return null;
      }
      return (
        <div className="assessment-complete-instructions">
          <p>
            You're in the Ora cohort! 🎉 You'll study daily with Ora for the
            next two weeks, starting tomorrow.
          </p>
          <p>
            Try to be consistent and finish your session each day. This will be
            great practice for the real exam!
          </p>
          <p>
            Click below to switch your exam to <strong>{stepExam.name}</strong>.
          </p>
          <p>Your post-assessment will appear here in two weeks.</p>
          <button
            className="button button--primary"
            onClick={() => {
              updateExam({
                userId: currentUser?.id,
                examId: stepExam.id,
                primary: true,
              });
            }}
          >
            Switch to {stepExam.name}
          </button>
        </div>
      );
    } else if (currentUser?.cohort === 'rct' && currentUser?.rctGroup === 'B') {
      return (
        <div className="assessment-complete-instructions">
          <p>
            You're in the UWorld cohort! Study daily with UWorld for the next
            two weeks, starting tomorrow.
          </p>
          <p>
            Try to be consistent and finish your session each day. This will be
            great practice for the real exam!
          </p>
          <p>
            After two weeks, come back here for your post-assessment—we'll email
            you a reminder!
          </p>
        </div>
      );
    }
    return null;
  };

  const renderTodaySession = () => {
    if (
      (todaySession.isComplete || isNoContent || todaySession.isSnoozed) &&
      !isEditingCustomSession &&
      !primaryExam?.hasCompletedRealExam
    ) {
      return (
        <div className="today-session_main_done">
          <div className="today-session_main_done_container">
            {!isEditingCustomSession && !primaryExam?.hasCompletedRealExam && (
              <>
                <div className="check-wrapper">
                  <AnimatedCheckmark />
                </div>
                <h1>
                  {todaySession.isSnoozed ? (
                    <>
                      <span>Enjoy your day!</span>
                    </>
                  ) : primaryExam?.isRCT ? (
                    'Assessment Complete'
                  ) : (
                    "You're Done for Today"
                  )}
                </h1>
                {primaryExam?.isRCT && renderAssessmentCompleteInstructions()}
              </>
            )}
            {!isNoContent &&
              !todaySession.isSnoozed &&
              !isEditingCustomSession &&
              !primaryExam?.isRCT && (
                <>
                  <div className="today-session_main_done_container_buttons">
                    <button
                      role="button"
                      className="button button--link"
                      tabIndex={0}
                      aria-label="View Today's Stats"
                      onClick={() => {
                        sendPhaseEvent({ type: 'START_SESSION_SUMMARY' });
                        hapticsImpactLight();
                      }}
                    >
                      Today's Stats
                      <ArrowRight />
                    </button>
                    <button
                      role="button"
                      tabIndex={0}
                      aria-label="Keep Studying"
                      className="button button--link"
                      onClick={() => {
                        setIsEditingCustomSession(true);
                        hapticsImpactLight();
                      }}
                    >
                      Keep Studying
                      <ArrowRight />
                    </button>
                  </div>
                  {(currentUser?.studyReminders !== 'daily' ||
                    !currentUser?.subscribedToEmails) && (
                    <div
                      style={{
                        marginTop: '1.5rem',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <button
                        className="button button--turn-on-reminders"
                        onClick={() => {
                          turnOnDailyStudyReminders();
                        }}
                        tabIndex={0}
                        aria-label="Turn on daily study reminders"
                      >
                        <Bell />
                        Turn on Reminders
                      </button>
                    </div>
                  )}
                </>
              )}
            {!isNoContent && todaySession.isSnoozed && (
              <button
                className="button button--link button--unsnooze"
                onClick={() => {
                  handleUnsnoozeSession();
                  hapticsImpactLight();
                }}
                style={{
                  display: 'flex',
                  margin: '0 auto',
                }}
                tabIndex={0}
                aria-label="Unsnooze Today's Session"
              >
                <Clock />
                Unsnooze Today's Session
              </button>
            )}
            {isNoContent && !primaryExam?.hasCompletedRealExam && (
              <p style={{ textAlign: 'center', padding: '0 2rem' }}>
                You don't have any due content today. Enjoy your day off!
              </p>
            )}
          </div>
          <div
            className={`content-type-highlight content-type-highlight--qbank`}
          ></div>
          <div
            className={`content-type-highlight content-type-highlight--flashcards`}
          ></div>
          <DotsPattern />
        </div>
      );
    } else {
      return (
        <>
          {isSuperAdmin(currentUser?.id) && videos.length > 0 && (
            <ContentType
              type={'video'}
              currentCount={
                videos.filter((video) => video?.watchState === 'COMPLETED')
                  .length
              }
              totalCount={videos.length}
              minCount={0}
              maxCount={videos.length}
              isEditingCustomSession={isEditingCustomSession}
              setIsEditingCustomSession={setIsEditingCustomSession}
              customCount={videos.length}
              setCustomCount={() => {}}
            />
          )}
          {(primaryExam?.isUsingQbank || isEditingCustomSession) && (
            <ContentType
              type={'qbank'}
              currentCount={
                activeBlock?.type === BlockType.timed
                  ? answeredVignettesCount
                  : submittedVignettesCount
              }
              totalCount={todaySession?.vignetteCount}
              minCount={modifyQBankMinimum}
              maxCount={320}
              isEditingCustomSession={isEditingCustomSession}
              setIsEditingCustomSession={setIsEditingCustomSession}
              customCount={customQBankCount}
              setCustomCount={setCustomQBankCount}
            />
          )}
          {(primaryExam?.isUsingFlashcards || isEditingCustomSession) && (
            <ContentType
              type={'flashcards'}
              currentCount={completedFlashcardsCount}
              totalCount={todaySession?.flashcardCount}
              minCount={completedFlashcardsCount}
              maxCount={500}
              isEditingCustomSession={isEditingCustomSession}
              setIsEditingCustomSession={setIsEditingCustomSession}
              customCount={customFlashcardCount}
              setCustomCount={setCustomFlashcardCount}
            />
          )}
        </>
      );
    }
  };

  const handleUnsnoozeSession = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await updateStudySession({
      id: todaySession.id,
      isSnoozed: false,
    });
    await refreshStudySessionAndContent();
  };

  const renderFooter = () => {
    return (
      <div className="today-session_footer">
        <button
          className="button button--secondary button--cancel"
          onClick={() => {
            setIsEditingCustomSession(false);
            hapticsImpactLight();
          }}
          tabIndex={0}
          aria-label="Cancel"
        >
          Cancel
        </button>
        {!isSaveDisabled && (
          <button
            className="button button--save"
            onClick={() => {
              hapticsImpactLight();
              handleSubmitCustomSession();
            }}
            disabled={isSaveDisabled}
          >
            Save
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={`today-session ${
          isEditingCustomSession ? 'today-session--editing' : ''
        }`}
      >
        {!isEditingCustomSession && !primaryExam?.isRCT && (
          <div className="today-session_header">
            <div className="today-session_header_left">
              {!todaySession.isSnoozed && (
                <button
                  ref={sessionSettingsRef}
                  className="button button--settings button--secondary"
                  onClick={() => {
                    setIsSessionSettingsMenuOpen(true);
                    hapticsImpactLight();
                  }}
                  disabled={currentUser?.cohort === 'rct'}
                  tabIndex={0}
                  aria-label="Open session settings"
                >
                  <Settings />
                </button>
              )}
              <Menu
                id="session-settings-menu"
                anchorEl={sessionSettingsRef.current}
                open={isSessionSettingsMenuOpen}
                onClose={() => setIsSessionSettingsMenuOpen(false)}
              >
                <MenuItem
                  onClick={() => {
                    setIsEditingCustomSession(true);
                    hapticsImpactLight();
                    setIsSessionSettingsMenuOpen(false);
                  }}
                >
                  <Settings />
                  Modify Session
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleSnoozeSession();
                    hapticsImpactLight();
                    setIsSessionSettingsMenuOpen(false);
                  }}
                >
                  <Clock />
                  Snooze Session
                </MenuItem>
              </Menu>
              <div className="today-session_header_left_text">
                <div className="today-session_header_left_text_title">
                  Today's Session
                </div>
                <div className="today-session_header_left_text_subtitle">
                  {moment(primaryExam?.examDate).diff(moment(), 'days') > 0
                    ? `${moment(primaryExam?.examDate).diff(moment(), 'days')} days until your ${primaryExam?.name} exam`
                    : primaryExam?.examDate
                      ? ''
                      : 'Select an exam date for better autopilot'}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="today-session_main">{renderTodaySession()}</div>
        {isEditingCustomSession && windowWidth > 560 && renderFooter()}
      </div>
      <LecturePanel isEditingCustomSession={isEditingCustomSession} />
      {isEditingCustomSession &&
        windowWidth <= 560 &&
        createPortal(renderFooter(), document.body)}
    </>
  );
};

export default TodaySession;
